import { useCallback, useRef, useState } from 'react';
import { Avatar } from '../../components/Avatar';
import { ChevronDownIcon, LogOutIcon } from '../../components/Icon';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { fmt } from '../../lib/money';
import type { Account } from '../../types/api';

interface Props {
  me: Account;
  balances: Record<string, number>;
  onSignOut: () => void;
}

/**
 * Shows who is signed in, and lets them sign out. There is no one-click
 * "switch to" any more - a real password gates every account now, so getting
 * to someone else's wallet means signing out and back in as them.
 */
export function AccountMenu({ me, balances, onSignOut }: Props) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(
    wrapperRef,
    useCallback(() => setOpen(false), []),
    open,
  );

  const balanceOf = (account: Account) => balances[account.id] ?? account.balanceCents;

  return (
    <div className="bell" ref={wrapperRef}>
      <button
        id="switch-user"
        className="ghost small account-trigger"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Signed in as ${me.name}. Switch account.`}
      >
        <Avatar name={me.name} />
        <span id="me-name">{me.name}</span>
        <ChevronDownIcon size={13} />
      </button>

      <div
        id="account-menu"
        className={`panel account-panel${open ? '' : ' hidden'}`}
        role="menu"
        aria-label="Account"
      >
        <div className="account-current">
          <Avatar name={me.name} />
          <div className="grow stack" style={{ minWidth: 0 }}>
            <div className="small truncate">{me.name}</div>
            <div className="tiny muted">{fmt(balanceOf(me))}</div>
          </div>
        </div>

        <button
          id="sign-out"
          className="menu-item danger-text"
          role="menuitem"
          onClick={() => {
            setOpen(false);
            onSignOut();
          }}
        >
          <LogOutIcon size={15} />
          <span className="grow" style={{ textAlign: 'left' }}>
            Sign out
          </span>
        </button>
      </div>
    </div>
  );
}
